import * as Msal from 'msal';
import _ from 'lodash';

export type DataObject = {
    isAuthenticated: boolean;
    accessToken: string;
    user: object;
    custom: { [key: string]: object | null };
}

export interface Request {
    scopes?: string[];
}

export interface Callbacks {
    // eslint-disable-next-line
    onAuthentication: (ctx: object, error: Msal.AuthError, response: Msal.AuthResponse) => any;
    // eslint-disable-next-line
    onToken: (ctx: object, error: Msal.AuthError | null, response: Msal.AuthResponse | null) => any;
    // eslint-disable-next-line
    onBeforeSignOut: (ctx: object) => any;
}

export interface Options {
    configuration: Msal.Configuration;
    autoRefreshToken: boolean;
    callbacks: Callbacks;
    request: Request;
}

export type CallbackQueueObject = {
    id: string;
    callback: string;
    // eslint-disable-next-line
    arguments: any[];
}

export interface MSALBase {
    options: Options;
    data: DataObject;
    signIn: () => void;
    // eslint-disable-next-line
    signOut: () => Promise<any> | void;
    isAuthenticated: () => boolean;
    acquireToken: (request: Request) => Promise<string | boolean>;
    // eslint-disable-next-line
    saveCustomData: (key: string, data: any) => void;
    requireAuthOnInitialize?: boolean;
}

export class UserAgentApplicationExtended extends Msal.UserAgentApplication {
    // eslint-disable-next-line
    public store: any = {};

    constructor(configuration: Msal.Configuration) {
        super(configuration);

        this.store = this.cacheStorage
    }
}

export class MSAL implements MSALBase {
    // eslint-disable-next-line
    private app: UserAgentApplicationExtended;
    private tokenExpirationTimer: undefined | number = undefined;
    public data: DataObject = {
        isAuthenticated: false,
        accessToken: '',
        user: {},
        custom: {}
    };
    public callbackQueue: CallbackQueueObject[] = [];

    constructor(public readonly options: Options) {
        if (!options) {
            throw new Error('options are required');
        }

        if (!options.configuration.auth.clientId) {
            throw new Error('options.configuration.auth.clientId is required');
        }

        if (!options.request) options.request = {
            scopes: ['user.read']
        };

        this.app = new UserAgentApplicationExtended({
            auth: {
                clientId: options.configuration.auth.clientId,
                authority: options.configuration.auth.authority,
                validateAuthority: options.configuration.auth.validateAuthority,
                redirectUri: options.configuration.auth.redirectUri,
                postLogoutRedirectUri: options.configuration.auth.postLogoutRedirectUri,
                navigateToLoginRequestUrl: options.configuration.auth.navigateToLoginRequestUrl
            },
            cache: options.configuration.cache,
            system: options.configuration.system,
            framework: options.configuration.framework
        });

        this.getSavedCallbacks();
        this.executeCallbacks();

        // Register Callbacks for redirect flow
        this.app.handleRedirectCallback((error: Msal.AuthError, response: Msal.AuthResponse | undefined) => {
            this.saveCallback('callbacks.onAuthentication', error, response);
        });

        this.data.isAuthenticated = this.isAuthenticated();
        if (this.data.isAuthenticated) {
            this.data.user = this.app.getAccount();
            this.acquireToken();
        }

        this.getStoredCustomData();
    }

    signIn() {
        if (!this.app.isCallback(window.location.hash) && !this.app.getAccount()) {
            // request can be used for login or token request, however in more complex situations this can have diverging options
            this.app.loginRedirect(this.options.request);
        }
    }

    async signOut() {
        if (this.options.callbacks && this.options.callbacks.onBeforeSignOut) {
            await this.options.callbacks.onBeforeSignOut(this);
        }
        this.app.logout();
    }

    isAuthenticated() {
        return !this.app.isCallback(window.location.hash) && !!this.app.getAccount();
    }

    async acquireToken(request = this.options.request) {
        try {
            //Always start with acquireTokenSilent to obtain a token in the signed in user from cache
            const response = await this.app.acquireTokenSilent(request);

            if (this.data.accessToken !== response.accessToken) {
                this.setAccessToken(response.accessToken, response.expiresOn, response.scopes);
                this.saveCallback('callbacks.onToken', null, response);
            }

            return response.accessToken;
        } catch (error) {
            // Upon acquireTokenSilent failure (due to consent or interaction or login required ONLY)
            // Call acquireTokenRedirect
            if (this.requiresInteraction(error.errorCode)) {
                this.app.acquireTokenRedirect(request); //acquireTokenPopup
                console.log(error); // audit for session timeout bug
            }
            else {
                this.saveCallback('callbacks.onToken', error, null);
            }

            return false;
        }
    }

    private requiresInteraction(errorCode: string) {
        if (!errorCode || !errorCode.length) {
            return false;
        }
        return errorCode === "consent_required" ||
            errorCode === "interaction_required" ||
            errorCode === "login_required";
    }

    private setAccessToken(accessToken: string, expiresOn: Date, scopes: string[]) {
        this.data.accessToken = accessToken;
        const expirationOffset = (this.app.getCurrentConfiguration().system?.tokenRenewalOffsetSeconds ?? 300) * 1000;
        const expiration = expiresOn.getTime() - (new Date()).getTime() - expirationOffset;
        if (this.tokenExpirationTimer) clearTimeout(this.tokenExpirationTimer);
        this.tokenExpirationTimer = window.setTimeout(() => {
            if (this.options.autoRefreshToken) {
                this.acquireToken({ scopes });
            } else {
                this.data.accessToken = '';
            }
        }, expiration)
    }

    // CUSTOM DATA
    // eslint-disable-next-line
    saveCustomData(key: string, data: any) {
        if (!Object.prototype.hasOwnProperty.call(this.data.custom, key)) {
            this.data.custom[key] = null;
        }
        this.data.custom[key] = data;
        this.storeCustomData();
    }

    private storeCustomData() {
        if (!_.isEmpty(this.data.custom)) {
            this.app.store.setItem('msal.custom', JSON.stringify(this.data.custom));
        } else {
            this.app.store.removeItem('msal.custom');
        }
    }

    private getStoredCustomData() {
        let customData = {};
        const customDataStr = this.app.store.getItem('msal.custom');
        if (customDataStr) {
            customData = JSON.parse(customDataStr);
        }
        this.data.custom = customData;
    }

    // CALLBACKS
    // eslint-disable-next-line
    private saveCallback(callbackPath: string, ...args: any[]) {
        if (_.get(this.options, callbackPath)) {
            const callbackQueueObject: CallbackQueueObject = {
                id: _.uniqueId(`cb-${callbackPath}`),
                callback: callbackPath,
                arguments: args
            };
            this.callbackQueue.push(callbackQueueObject);
            this.storeCallbackQueue();
            this.executeCallbacks([callbackQueueObject]);
        }
    }

    private getSavedCallbacks() {
        const callbackQueueStr = this.app.store.getItem('msal.callbackqueue');
        if (callbackQueueStr) {
            this.callbackQueue = [...this.callbackQueue, ...JSON.parse(callbackQueueStr)];
        }
    }

    private async executeCallbacks(callbacksToExec: CallbackQueueObject[] = this.callbackQueue) {
        if (callbacksToExec.length) {
            for (const i in callbacksToExec) {
                const cb = callbacksToExec[i];
                const callback = _.get(this.options, cb.callback);
                try {
                    await callback(this, ...cb.arguments);
                    _.remove(this.callbackQueue, function (currentCb: CallbackQueueObject) {
                        return cb.id === currentCb.id;
                    });
                    this.storeCallbackQueue();
                } catch (e) {
                    console.warn(`Callback '${cb.id}' failed with error: `, e.message);
                }
            }
        }
    }

    private storeCallbackQueue() {
        if (this.callbackQueue.length) {
            this.app.store.setItem('msal.callbackqueue', JSON.stringify(this.callbackQueue));
        } else {
            this.app.store.removeItem('msal.callbackqueue');
        }
    }
}

export default class MsalPlugin {
    // eslint-disable-next-line
    static install(Vue: any, options: Options): void {
        Vue.prototype.$msal = new MsalPlugin(options, Vue);
    }

    // eslint-disable-next-line
    constructor(options: Options, Vue: any = undefined) {
        const msal = new MSAL(options);

        const exposed: MSALBase = {
            options: options,
            data: msal.data,
            signIn() { msal.signIn(); },
            async signOut() { await msal.signOut(); },
            isAuthenticated() { return msal.isAuthenticated(); },
            async acquireToken(request) { return await msal.acquireToken(request); },
            // eslint-disable-next-line
            saveCustomData(key: string, data: any) { msal.saveCustomData(key, data); }
        };

        return exposed;
    }
}
